import React, { useState, useEffect } from 'react';
import { automunityAPI } from '../lib/api';
import './Settings.css';

function asArray<T>(val: unknown): T[] {
  if (Array.isArray(val)) return val as T[];
  if (val && typeof val === 'object') {
    const obj = val as any;
    if (Array.isArray(obj.records)) return obj.records as T[];
    if (typeof obj.records === 'string') {
      try { return JSON.parse(obj.records) as T[]; } catch { return []; }
    }
  }
  return [];
}

interface Role {
  role_id: number;
  name: string;
}

interface Badge {
  badge_id: number;
  name: string;
  description: string;
  logo: string;
}

interface Stage {
  stage_id: number;
  name: string;
  description: string;
}

interface Service {
  service_id: number;
  name: string;
}

interface Team {
  team_id: number;
  name: string;
  full_name: string | null;
  abbreviation: string | null;
}

interface Host {
  host_id: number;
  name: string;
}

interface RequestStatus {
  request_status_id: number;
  name: string;
  label: string;
  color: string;
  sort_order: number;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('teams');
  const [roles, setRoles] = useState<Role[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [requestStatuses, setRequestStatuses] = useState<RequestStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{type: string, id: number, name: string} | null>(null);
  const [createType, setCreateType] = useState('');
  const [formData, setFormData] = useState<any>({});
  const [kiroToken, setKiroToken] = useState<string | null>(null);
  const [tokenVisible, setTokenVisible] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);

        
        const [badgesRes, stagesRes, servicesRes, teamsRes, hostsRes] = await Promise.all([
          automunityAPI.getBadges(),
          automunityAPI.getStages(), 
          automunityAPI.getServices(),
          automunityAPI.getTeams(),
          automunityAPI.getHosts()
        ]);

        const rolesRes = await automunityAPI.getRoles();
        const rolesData = await rolesRes.body.json();
        
        const badgesData = await badgesRes.body.json();
        const stagesData = await stagesRes.body.json();
        const servicesData = await servicesRes.body.json();
        const teamsData = await teamsRes.body.json();
        const hostsData = await hostsRes.body.json();

        setRoles(asArray<Role>(rolesData));
        setBadges(asArray<Badge>(badgesData));
        setStages(asArray<Stage>(stagesData));
        setServices(asArray<Service>(servicesData));
        setTeams(asArray<Team>(teamsData));
        setHosts(asArray<Host>(hostsData));

        // Fetch request statuses
        try {
          const statusesRes = await automunityAPI.getRequestStatuses();
          const statusesData = await statusesRes.body.json();
          setRequestStatuses(asArray<RequestStatus>(statusesData));
        } catch { /* table may not exist yet */ }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Fetch kiro token when developer tab is selected
  useEffect(() => {
    if (activeTab === 'developer' && kiroToken === null) {
      const fetchToken = async () => {
        setTokenLoading(true);
        try {
          const res = await automunityAPI.getKiroToken();
          const data = await res.body.json();
          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
          setKiroToken(parsed.kiro_api_token || null);
        } catch (err) {
          console.error('Error fetching token:', err);
        } finally {
          setTokenLoading(false);
        }
      };
      fetchToken();
    }
  }, [activeTab]);

  const handleCreate = (type: string) => {
    setCreateType(type);
    setFormData({});
    setShowCreateModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      switch (createType) {
        case 'role':
          await automunityAPI.createRole(formData);
          const rolesRes = await automunityAPI.getRoles();
          const rolesData = await rolesRes.body.json();
          setRoles(asArray<Role>(rolesData));
          break;
        case 'badge':
          await automunityAPI.createBadge(formData);
          const badgesRes = await automunityAPI.getBadges();
          const badgesData = await badgesRes.body.json();
          setBadges(asArray<Badge>(badgesData));
          break;
        case 'stage':
          await automunityAPI.createStage(formData);
          const stagesRes = await automunityAPI.getStages();
          const stagesData = await stagesRes.body.json();
          setStages(asArray<Stage>(stagesData));
          break;
        case 'service':
          await automunityAPI.createService(formData);
          const servicesRes = await automunityAPI.getServices();
          const servicesData = await servicesRes.body.json();
          setServices(asArray<Service>(servicesData));
          break;
        case 'team':
          console.log('Creating team with formData:', formData);
          const createTeamRes = await automunityAPI.createTeam(formData);
          console.log('Create team response status:', createTeamRes.statusCode);
          const createTeamBody = await createTeamRes.body.json();
          console.log('Create team response body:', createTeamBody);
          const teamsRes = await automunityAPI.getTeams();
          const teamsData = await teamsRes.body.json();
          console.log('Get teams response:', teamsData);
          setTeams(asArray<Team>(teamsData));
          break;
        case 'host':
          await automunityAPI.createHost(formData);
          const hostsRes = await automunityAPI.getHosts();
          const hostsData = await hostsRes.body.json();
          setHosts(asArray<Host>(hostsData));
          break;
      }
      
      setShowCreateModal(false);
      setFormData({});
    } catch (error) {
      console.error('Error creating item:', error);
      alert('Failed to create item. Please try again.');
    }
  };

  const handleDelete = async (type: string, id: number) => {
    try {
      switch (type) {
        case 'role':
          await automunityAPI.deleteRole(id);
          setRoles(roles.filter(r => r.role_id !== id));
          break;
        case 'badge':
          await automunityAPI.deleteBadge(id);
          setBadges(badges.filter(b => b.badge_id !== id));
          break;
        case 'stage':
          await automunityAPI.deleteStage(id);
          setStages(stages.filter(s => s.stage_id !== id));
          break;
        case 'service':
          await automunityAPI.deleteService(id);
          setServices(services.filter(s => s.service_id !== id));
          break;
        case 'team':
          await automunityAPI.deleteTeam(id);
          setTeams(teams.filter(t => t.team_id !== id));
          break;
        case 'host':
          await automunityAPI.deleteHost(id);
          setHosts(hosts.filter(h => h.host_id !== id));
          break;
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    }
  };

  const [editingItem, setEditingItem] = useState<{type: string, id: number, data: any} | null>(null);

  const handleInlineEdit = (type: string, item: any) => {
    const singularType = type.endsWith('ses') ? type.slice(0, -2) : type.slice(0, -1);
    setEditingItem({ type: singularType, id: item[`${singularType}_id`], data: { ...item } });
  };

  const handleInlineSave = async () => {
    if (!editingItem) return;
    try {
      const { type, id, data } = editingItem;
      switch (type) {
        case 'team':
          await automunityAPI.updateTeam(id, data);
          setTeams(teams.map(t => t.team_id === id ? { ...t, ...data } : t));
          break;
        case 'role':
          await automunityAPI.updateRole(id, data);
          setRoles(roles.map(r => r.role_id === id ? { ...r, ...data } : r));
          break;
        case 'service':
          await automunityAPI.updateService(id, data);
          setServices(services.map(s => s.service_id === id ? { ...s, ...data } : s));
          break;
        case 'host':
          await automunityAPI.updateHost(id, data);
          setHosts(hosts.map(h => h.host_id === id ? { ...h, ...data } : h));
          break;
        case 'stage':
          await automunityAPI.updateStage(id, data);
          setStages(stages.map(s => s.stage_id === id ? { ...s, ...data } : s));
          break;
        case 'badge':
          await automunityAPI.updateBadge(id, data);
          setBadges(badges.map(b => b.badge_id === id ? { ...b, ...data } : b));
          break;
      }
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save changes.');
    }
  };

  const handleInlineCancel = () => {
    setEditingItem(null);
  };

  const renderTable = (data: any[], columns: string[], type: string, displayTitle?: string) => {
    const singularType = type.endsWith('ses') ? type.slice(0, -2) : type.slice(0, -1);
    const singularLabel = singularType.replace(/_/g, ' ');
    const title = displayTitle || (type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' '));
    const idCol = `${singularType}_id`;
    const editableColumns = columns.filter(col => !col.endsWith('_id'));

    return (
      <div className="settings-table">
        <div className="table-header">
          <h3>{title}</h3>
          <button 
            className="btn btn-primary"
            onClick={() => handleCreate(singularType)}
          >
            Create {singularLabel}
          </button>
        </div>
        {data.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
            <p style={{ margin: 0, fontSize: '16px' }}>No data available</p>
            <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>Click "Create {singularLabel}" to add a new item</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col}>{col.replace(/_/g, ' ').toUpperCase()}</th>
                ))}
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                const isEditing = editingItem?.type === singularType && editingItem?.id === item[idCol];
                return (
                  <tr key={index}>
                    {columns.map(col => (
                      <td key={col}>
                        {isEditing && editableColumns.includes(col) ? (
                          <input
                            type="text"
                            value={editingItem.data[col] || ''}
                            onChange={(e) => setEditingItem({
                              ...editingItem,
                              data: { ...editingItem.data, [col]: e.target.value }
                            })}
                            style={{ width: '100%', padding: '4px 8px', border: '1px solid #4a90d9', borderRadius: '3px', fontSize: '13px' }}
                            autoFocus={col === editableColumns[0]}
                          />
                        ) : col === 'logo' ? (
                          <span className="badge-icon">{item[col]}</span>
                        ) : col === 'color' ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: item[col], display: 'inline-block' }}></span>
                            {item[col]}
                          </span>
                        ) : (
                          <span style={{ cursor: 'pointer' }} onDoubleClick={() => handleInlineEdit(type, item)}>
                            {item[col] || <span style={{ color: '#ccc' }}>—</span>}
                          </span>
                        )}
                      </td>
                    ))}
                    <td>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            className="btn btn-primary"
                            onClick={handleInlineSave}
                            style={{ padding: '4px 10px', fontSize: '12px' }}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={handleInlineCancel}
                            style={{ padding: '4px 10px', fontSize: '12px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleInlineEdit(type, item)}
                            style={{ padding: '4px 10px', fontSize: '12px' }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              const itemName = item.name || item[idCol];
                              setDeleteTarget({ type: singularType, id: item[idCol], name: itemName });
                              setShowDeleteModal(true);
                            }}
                            style={{ padding: '4px 10px', fontSize: '12px', color: '#dc3545' }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderCreateForm = () => {
    const getFormFields = () => {
      switch (createType) {
        case 'role':
        case 'service':
        case 'host':
          return [
            { name: 'name', label: 'Name', type: 'text', required: true }
          ];
        case 'team':
          return [
            { name: 'name', label: 'Name (short identifier)', type: 'text', required: true },
            { name: 'full_name', label: 'Full Name (e.g. Support Practice Group)', type: 'text', required: false },
            { name: 'abbreviation', label: 'Abbreviation (e.g. SPG)', type: 'text', required: false }
          ];
        case 'badge':
          return [
            { name: 'name', label: 'Name', type: 'text', required: true },
            { name: 'description', label: 'Description', type: 'text', required: true },
            { name: 'logo', label: 'Icon/Emoji', type: 'text', required: false }
          ];
        case 'stage':
          return [
            { name: 'name', label: 'Name', type: 'text', required: true },
            { name: 'description', label: 'Description', type: 'text', required: true }
          ];
        case 'request_status':
          return [
            { name: 'name', label: 'Internal Name (snake_case)', type: 'text', required: true },
            { name: 'label', label: 'Display Label', type: 'text', required: true },
            { name: 'color', label: 'Color (hex)', type: 'text', required: true },
            { name: 'sort_order', label: 'Sort Order', type: 'number', required: true }
          ];
        default:
          return [];
      }
    };

    return (
      <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Create {createType}</h3>
            <button 
              className="close-button"
              onClick={() => setShowCreateModal(false)}
            >
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {getFormFields().map(field => (
                <div key={field.name} className="form-group">
                  <label>{field.label}</label>
                  <input
                    type={field.type}
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      [field.name]: e.target.value
                    })}
                    required={field.required}
                    className="form-control"
                  />
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your Automunity configuration</p>
      </div>

      <div className="settings-tabs">
        <div className="tab-nav">
          {[
            { id: 'teams', label: 'Teams' },
            { id: 'roles', label: 'Roles' },
            { id: 'badges', label: 'Badges' },
            { id: 'stages', label: 'Stages' },
            { id: 'request_statuses', label: 'Statuses' },
            { id: 'services', label: 'Services' },
            { id: 'hosts', label: 'Hosts' },
            { id: 'developer', label: 'Developer API' }
          ].map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          <div className="portlet">
            <div className="portlet-body">
              {activeTab === 'teams' && renderTable(teams, ['team_id', 'name', 'full_name', 'abbreviation'], 'teams')}
              {activeTab === 'roles' && renderTable(roles, ['role_id', 'name', 'description'], 'roles')}
              {activeTab === 'badges' && renderTable(badges, ['badge_id', 'icon', 'name', 'description', 'category'], 'badges')}
              {activeTab === 'stages' && renderTable(stages, ['stage_id', 'name', 'description'], 'stages', 'Automation Stages')}
              {activeTab === 'services' && renderTable(services, ['service_id', 'name'], 'services')}
              {activeTab === 'hosts' && renderTable(hosts, ['host_id', 'name'], 'hosts')}
              {activeTab === 'request_statuses' && renderTable(requestStatuses, ['request_status_id', 'name', 'label', 'color', 'sort_order'], 'request_statuses', 'Request Statuses')}
              {activeTab === 'developer' && (
                <div className="settings-table">
                  <div className="table-header">
                    <h3>Kiro API Key</h3>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <p style={{ color: '#666', marginBottom: '16px' }}>
                      Your personal API key for the Automunity Developer Toolkit (Kiro Power). 
                      This key lets you interact with Automunity from your local environment without needing AWS production access.
                    </p>
                    <p style={{ color: '#666', marginBottom: '16px' }}>
                      Set it as <code>AUTOMUNITY_API_KEY</code> in your <code>.env</code> file inside <code>kiro-app-master/automunity-requests/</code>.
                    </p>
                    {kiroToken ? (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type={tokenVisible ? 'text' : 'password'}
                            value={kiroToken}
                            readOnly
                            style={{ fontFamily: 'monospace', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', width: '360px', fontSize: '14px' }}
                          />
                          <button
                            className="btn btn-secondary"
                            onClick={() => setTokenVisible(!tokenVisible)}
                            style={{ padding: '8px 12px' }}
                          >
                            {tokenVisible ? 'Hide' : 'Show'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => { navigator.clipboard.writeText(kiroToken); }}
                            style={{ padding: '8px 12px' }}
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p style={{ color: '#999', fontStyle: 'italic', marginBottom: '16px' }}>No API key generated yet.</p>
                    )}
                    <button
                      className="btn btn-primary"
                      disabled={tokenLoading}
                      onClick={async () => {
                        if (kiroToken && !window.confirm('This will revoke your current key and generate a new one. Continue?')) return;
                        setTokenLoading(true);
                        try {
                          const res = await automunityAPI.regenerateKiroToken();
                          const data = await res.body.json();
                          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                          setKiroToken(parsed.kiro_api_token);
                          setTokenVisible(true);
                        } catch (err) {
                          console.error('Error generating key:', err);
                          alert('Failed to generate API key. Contact an admin if this persists.');
                        } finally {
                          setTokenLoading(false);
                        }
                      }}
                    >
                      {tokenLoading ? 'Generating...' : kiroToken ? 'Regenerate Key' : 'Generate API Key'}
                    </button>
                    {kiroToken && (
                      <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontFamily: 'monospace', fontSize: '13px' }}>
                        <p style={{ margin: '0 0 8px 0', fontFamily: 'inherit', fontWeight: 'bold' }}>.env setup:</p>
                        <code>AUTOMUNITY_API_URL=https://hcjr3glo1i.execute-api.us-east-1.amazonaws.com/v1</code><br/>
                        <code>AUTOMUNITY_API_KEY={tokenVisible ? kiroToken : '••••••••••••••••••••'}</code>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && renderCreateForm()}
      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button 
                className="close-button"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteTarget.name}</strong>?</p>
              <p style={{ color: '#666', fontSize: '14px' }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => handleDelete(deleteTarget.type, deleteTarget.id)}
                style={{ backgroundColor: '#dc3545' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;